import type { FC } from "react";
import { useTranslation } from "@privacy/translations";
import { Autocomplete } from "@mui/material";
import { useController, useFormContext } from "react-hook-form";
import { autocompleteClasses } from "@mui/material/Autocomplete";
import type { InputProps } from "../DynamicInput";
import { useGetTenantsHierarchyList } from "../../api/tenants";
import { useUpdateRegistrationAnswers } from "../../api/registrations";
import { TenantIcon3 } from "../../shared/icons";
import { TenantOption, filter, flattenTenantHierarchy } from "../../shared/TenantSelect";

import {
  inlineInputStateStyles,
  useInlineInputState,
  type InlineInputStateStyles,
} from "../inlineForm/inputs/InlineInputState";
import { DynamicShared } from "../../dynamicShared";
import { AutoCompleteTextField } from "./SingleAutocomplete";

export const TenantMultiSelect: FC<
  InputProps<string[]> & { inlineInputStateStyles?: InlineInputStateStyles }
> = props => {
  const { t } = useTranslation();
  const { inputState, setIsEditing } = useInlineInputState(props.readonly);
  const { data: tenantHierarchy } = useGetTenantsHierarchyList();
  const flattenedNodes = tenantHierarchy ? flattenTenantHierarchy(tenantHierarchy) : [];
  const registrationAnswersMutation = useUpdateRegistrationAnswers();

  const { control, setValue } = useFormContext();
  const { field } = useController({
    control,
    name: props.questionCard.id,
  });

  const selected = field.value ? flattenedNodes.filter(flattenedNode => field.value.includes(flattenedNode.id)) : [];

  return (
    <Autocomplete
      fullWidth
      multiple
      noOptionsText={t("TenantSelect.NoOptions")}
      clearIcon={null}
      id={props.questionCard.id}
      disablePortal
      openOnFocus
      disabled={props.readonly || registrationAnswersMutation.isPending}
      disableClearable={Boolean(selected.length)}
      disableCloseOnSelect
      options={flattenedNodes}
      value={selected}
      onBlur={() => {
        setIsEditing(false);
        props.onSave(selected.map(option => option.id));
      }}
      onFocus={() => setIsEditing(true)}
      getOptionLabel={option => option.organizationName}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_, selectedOptions) => {
        const selectedValues = selectedOptions.map(option => option.id);
        setValue(props.questionCard.id, selectedValues, { shouldDirty: true });
      }}
      filterOptions={(options, state) => filter(options, state.inputValue)}
      renderValue={(selected, getItemProps) => {
        return selected.map((option, index) => {
          const { onDelete, key, tabIndex } = getItemProps({ index });
          return (
            <DynamicShared.Chip
              key={key}
              renderIcon={iconProps => <TenantIcon3 {...iconProps} />}
              label={option.organizationName}
              tabIndex={tabIndex}
              onDelete={onDelete}
              inputState={inputState}
              variant="entity"
            />
          );
        });
      }}
      renderInput={params => (
        <AutoCompleteTextField
          params={params}
          isSaving={registrationAnswersMutation.isPending}
          inputRef={field.ref}
          onBlur={field.onBlur}
          isError={registrationAnswersMutation.isError}
          inputState={inputState}
          isEmpty={!selected.length}
          placeholderKey={selected.length ? undefined : "DynamicForm.Inline.InputPlaceholder"}
          renderIcon={props => (selected.length ? undefined : <TenantIcon3 {...props} />)}
          inlineInputStateStyles={props.inlineInputStateStyles ? inlineInputStateStyles : undefined}
          sx={{
            /** We do not want the input to take space when not editing, but it is needed to show empty placeholder as well*/ [`&.${autocompleteClasses.inputRoot}`]:
              { minHeight: "36px", gap: !selected.length ? 0 : 1 },
            [`& .${autocompleteClasses.input}`]: {
              height: inputState === "editing" || !selected.length ? "auto" : 0,
              py: "0 !important",
            },
          }}
        />
      )}
      renderOption={({ key, ...optionsProps }, option) => (
        <TenantOption key={key} option={option} optionsProps={optionsProps} tenantHierarchy={tenantHierarchy} />
      )}
    />
  );
};
